import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Helmet } from "react-helmet-async";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Check, Clock, DollarSign, Info, Link as LinkIcon, Loader2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

// Define the validation schema using Zod
const requestFormSchema = z.object({
  niche: z.string().min(1, { message: "Please select a niche" }),
  linkType: z.string().min(1, { message: "Please select a link type" }),
  drMin: z.number().min(0).max(100),
  daMin: z.number().min(0).max(100),
  budgetMin: z.number().min(0, { message: "Minimum budget must be positive" }),
  budgetMax: z.number().min(0, { message: "Maximum budget must be positive" })
    .refine(val => val > 0, { message: "Maximum budget must be greater than zero" }),
  notes: z.string().optional(),
  expiresIn: z.number().int().min(1)
}).refine(data => data.budgetMax >= data.budgetMin, {
  message: "Maximum budget must be greater than or equal to minimum budget",
  path: ["budgetMax"]
});

// Extract type from the schema
type RequestFormValues = z.infer<typeof requestFormSchema>;

const CreateRequest = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize the form with react-hook-form
  const form = useForm<RequestFormValues>({
    resolver: zodResolver(requestFormSchema),
    defaultValues: {
      niche: "",
      linkType: "Guest Post",
      drMin: 50,
      daMin: 45,
      budgetMin: 100,
      budgetMax: 200,
      notes: "",
      expiresIn: 30,
    },
  });

  // Form submission handler
  const onSubmit = async (data: RequestFormValues) => {
    setIsSubmitting(true);
    try {
      // In a real app, this would make an API call
      console.log("Form submitted:", data);
      
      toast({
        title: "Request Created",
        description: "Your backlink request has been created successfully",
      });
      
      // Reset form or redirect
      // form.reset(); // Uncomment to reset the form
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "There was a problem creating your request",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Backlink Request | SEO Link Market</title>
      </Helmet>
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold mb-6">Create a Backlink Request</h1>
            <p className="text-gray-600 mb-8">
              Let sellers know what kind of backlink opportunities you're looking for and get relevant offers directly in your inbox.
            </p>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <Card>
                  <CardHeader>
                    <CardTitle>Request Details</CardTitle>
                    <CardDescription>
                      Provide information about your backlink needs
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <FormField
                      control={form.control}
                      name="niche"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormLabel>Niche/Industry <span className="text-red-500">*</span></FormLabel>
                          <Select 
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a niche" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Tech">Technology</SelectItem>
                              <SelectItem value="Finance">Finance</SelectItem>
                              <SelectItem value="Health">Health & Wellness</SelectItem>
                              <SelectItem value="Travel">Travel</SelectItem>
                              <SelectItem value="E-commerce">E-commerce</SelectItem>
                              <SelectItem value="Education">Education</SelectItem>
                              <SelectItem value="Marketing">Marketing</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="linkType"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormLabel>Link Type</FormLabel>
                          <Select 
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Guest Post">Guest Post</SelectItem>
                              <SelectItem value="Niche Edit">Niche Edit</SelectItem>
                              <SelectItem value="Resource Page">Resource Page</SelectItem>
                              <SelectItem value="Editorial">Editorial</SelectItem>
                              <SelectItem value="Any">Any Type</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="drMin"
                      render={({ field }) => (
                        <FormItem className="space-y-4">
                          <div className="flex items-center justify-between">
                            <FormLabel>Minimum DR (Domain Rating)</FormLabel>
                            <span className="text-primary font-semibold">{field.value}</span>
                          </div>
                          <FormControl>
                            <Slider
                              value={[field.value]}
                              min={0}
                              max={100}
                              step={5}
                              onValueChange={(value) => field.onChange(value[0])}
                              className="w-full"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="daMin"
                      render={({ field }) => (
                        <FormItem className="space-y-4">
                          <div className="flex items-center justify-between">
                            <FormLabel>Minimum DA (Domain Authority)</FormLabel>
                            <span className="text-primary font-semibold">{field.value}</span>
                          </div>
                          <FormControl>
                            <Slider
                              value={[field.value]}
                              min={0}
                              max={100}
                              step={5}
                              onValueChange={(value) => field.onChange(value[0])}
                              className="w-full"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="space-y-2">
                      <Label>Budget Range</Label>
                      <div className="flex gap-4 items-center">
                        <div className="relative flex-1">
                          <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                          <FormField
                            control={form.control}
                            name="budgetMin"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    type="number"
                                    className="pl-9"
                                    {...field}
                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <span>to</span>
                        <div className="relative flex-1">
                          <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                          <FormField
                            control={form.control}
                            name="budgetMax"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    type="number"
                                    className="pl-9"
                                    {...field}
                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormLabel>Additional Requirements/Notes</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Describe any specific requirements, target audience, or other details..."
                              className="min-h-[120px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="expiresIn"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormLabel>Request Expires In</FormLabel>
                          <Select 
                            onValueChange={(value) => field.onChange(Number(value))}
                            defaultValue={field.value.toString()}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="7">7 days</SelectItem>
                              <SelectItem value="14">14 days</SelectItem>
                              <SelectItem value="30">30 days</SelectItem>
                              <SelectItem value="60">60 days</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                  <CardFooter className="flex flex-col sm:flex-row gap-4 justify-end items-center">
                    <Button type="button" variant="outline">Save as Draft</Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Create Request
                    </Button>
                  </CardFooter>
                </Card>
              </form>
            </Form>
            
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mt-6">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-sm">How it works</h3>
                  <ul className="mt-2 space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Create your request with specific requirements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <LinkIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Sellers with matching sites will contact you with offers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Clock className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Review offers and choose the best match for your needs</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateRequest;
